import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { HSGBanner, HSGSmallBanner } from '@shared/assets';
import { FilledRightArrowIcon } from '@shared/assets-icon';
import { Button } from '@shared/components';
import { HEALTHIERSG_ENROLLMENT_INTROSPLASH_PATH } from '@shared/constants/paths';

const { Text, Title } = Typography;

interface HsgBannerProps {
  hasHealthPlan: boolean;
  isDeromActive: boolean;
}

const HsgBanner = ({ hasHealthPlan, isDeromActive }: HsgBannerProps) => {
  const { t } = useTranslation('home');
  const navigate = useNavigate();

  const titleText = hasHealthPlan ? t('keepTrackOfYourHealthGoals') : t('haveYouEnrolledInHealthierSg');

  const subtitleText = hasHealthPlan ? t('takeProactiveStepsHealthPlan') : t('enrolInHealthierSg');

  const extraText = hasHealthPlan ? (
    <Text className="text-base">{t('workHealthierSgDoctor')}</Text>
  ) : (
    <>
      <Text className="text-base">{t('takeProactiveSteps')}</Text>
      <Text className="text-sm">{t('forServicesOutsideTheScope')}</Text>
      <Text className="text-sm">{t('prevailingEligibilityCriteria')}</Text>
    </>
  );

  const buttonLabel = hasHealthPlan ? t('viewHealthPlan') : t('enterHealthierSg');

  const handleNavigate = () => navigate(HEALTHIERSG_ENROLLMENT_INTROSPLASH_PATH);

  const renderButton = () => (
    <Button
      className="flex items-center justify-between gap-2 rounded-5xl border-none bg-secondary-green-100 p-1 pl-4 text-secondary-green-500 md:min-w-60 lg:gap-8 lg:pl-8"
      onClick={handleNavigate}
      variant="secondary"
    >
      <Text className="text-sm whitespace-normal break-words">{buttonLabel}</Text>
      <div className="rounded-r-5xl bg-primary-green-4 p-3">
        <FilledRightArrowIcon />
      </div>
    </Button>
  );

  return (
    <div>
      {isDeromActive ? (
        <div className="flex h-full flex-col justify-between rounded-bl-5xl rounded-tr-5xl border-4 border-[#F2FEF5] bg-[#F2FEF5] pt-6">
          <div className="flex flex-col gap-2 px-4 pb-6">
            <img
              alt="Illustration of the Healthier SG logo with people doing healthy activities - exercising, eating fruit, doing yoga, and cycling in a park setting."
              className="h-auto max-w-full"
              src={HSGSmallBanner}
            />
            <Title className="m-0 px-2 pt-4 text-2xl text-primary-blue-grey lg:text-2.5xl" level={3}>
              {titleText}
            </Title>
            <Text className="px-2 pb-2 text-base font-semibold">{subtitleText}</Text>
            <div className="px-2">{extraText}</div>
          </div>
          <div className="flex justify-center rounded-bl-5xl bg-secondary-green p-4 ">{renderButton()}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-7 rounded-bl-5xl rounded-tr-5xl border-4 border-white bg-[#F2FEF5] lg:relative">
          <img
            alt="Illustration of the Healthier SG logo with people doing healthy activities - exercising, eating fruit, doing yoga, and cycling in a park setting."
            className="md:max-w-100 lg:w-100 lg:max-w-100 bottom-0 left-8 h-auto max-w-full self-center px-4 pt-5 lg:absolute lg:px-0"
            src={HSGBanner}
          />
          <div className="lg:ml-114 flex flex-col gap-2 px-4 lg:pr-10 lg:pt-8">
            <Title className="m-0 text-2xl text-primary-blue-grey lg:text-2.5xl" level={3}>
              {titleText}
            </Title>
            <Text className="text-base font-semibold">{subtitleText}</Text>
            {extraText}
          </div>
          <div className="flex justify-center rounded-bl-5xl bg-secondary-green p-4 lg:justify-normal">
            <div className="lg:ml-114">{renderButton()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HsgBanner;
